import { useRef, useCallback, useEffect } from 'preact/compat';

export type UseModalProps = Readonly<{
  /**
   * Determines whether the Modal is open.
   */
  isOpen: boolean;
  /**
   * On backdrop click callback.
   */
  onBackdropClick?: (event: MouseEvent) => void;
}>;

/**
 *
 * @param props
 */
export const useModal = (props: UseModalProps) => {
  const { isOpen, onBackdropClick: onBackdropClickProp } = props;

  const backdropRef = useRef<HTMLDivElement>(null);

  const mouseDownTarget = useRef<EventTarget | null>(null);

  const onMouseDown = useCallback((event: MouseEvent) => {
    if (backdropRef.current === event.target) {
      mouseDownTarget.current = event.target;
    }
  }, []);

  const onBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (backdropRef.current === event.target) {
        event.stopPropagation();
        // event should originate on the same target
        if (mouseDownTarget.current === event.target) {
          onBackdropClickProp?.(event);
        }
      }
    },
    [onBackdropClickProp]
  );

  useEffect(() => {
    const modal = backdropRef.current;

    if (modal) {
      modal.addEventListener('mousedown', onMouseDown);
      modal.addEventListener('click', onBackdropClick);

      return () => {
        modal.removeEventListener('mousedown', onMouseDown);
        modal.removeEventListener('click', onBackdropClick);
      };
    }

    return () => {};
  }, [isOpen, onMouseDown, onBackdropClick]);

  return {
    backdropRef
  };
};

export default useModal;
